import React, { useEffect, useState } from "react";
import { Building2, MailPlus, ShieldCheck, UserRoundCheck, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import { useI18n } from "@/lib/i18n";

const ROLES = ["admin", "manager", "reviewer", "member"];

export default function OrganizationPage() {
  const { t } = useI18n();
  const [organization, setOrganization] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [mine, setMine] = useState([]);
  const [name, setName] = useState("");
  const [invite, setInvite] = useState({ email: "", role: "member" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [org, memberItems, myItems] = await Promise.all([
        api.getOrganization(), api.listOrganizationMembers(), api.listMyOrganizationInvitations(),
      ]);
      setOrganization(org); setName(org.name); setMembers(memberItems); setMine(myItems);
      if (["owner", "admin"].includes(org.my_role)) {
        setInvitations(await api.listOrganizationInvitations());
      } else setInvitations([]);
    } catch { toast.error(t("org_load_failed")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const canManage = ["owner", "admin"].includes(organization?.my_role);
  const saveName = async (event) => {
    event.preventDefault();
    try { const updated = await api.updateOrganization({ name }); setOrganization({ ...organization, ...updated }); toast.success(t("org_saved")); }
    catch { toast.error(t("err_generic_save")); }
  };
  const sendInvite = async (event) => {
    event.preventDefault();
    try {
      const created = await api.inviteOrganizationMember(invite);
      setInvitations([created, ...invitations]); setInvite({ email: "", role: "member" }); toast.success(t("org_invited"));
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail?.code === "seat_limit_reached" ? t("org_seat_limit") : (typeof detail === "string" ? detail : t("err_generic_save")));
    }
  };
  const changeRole = async (member, role) => {
    try { const updated = await api.updateOrganizationMember(member.id, { role }); setMembers(members.map((item) => item.id === member.id ? updated : item)); }
    catch { toast.error(t("err_generic_save")); }
  };
  const revoke = async (item) => {
    await api.revokeOrganizationInvitation(item.id);
    setInvitations(invitations.filter((value) => value.id !== item.id));
  };
  const removeMember = async (member) => {
    if (!window.confirm(t("org_remove_confirm"))) return;
    try {
      await api.removeOrganizationMember(member.id);
      setMembers(members.filter((item) => item.id !== member.id));
      setOrganization({ ...organization, seats_used: Math.max(0, (organization.seats_used || 1) - 1) });
      toast.success(t("org_removed"));
    } catch { toast.error(t("err_generic_save")); }
  };
  const accept = async (item) => {
    try { await api.acceptOrganizationInvitation(item.id); window.location.reload(); }
    catch (error) { toast.error(error?.response?.data?.detail?.code === "workspace_not_empty" ? t("org_workspace_not_empty") : t("err_generic_save")); }
  };

  if (loading) return <div className="p-12 text-[var(--td-text-3)]">…</div>;
  return (
    <div className="px-4 py-6 sm:p-6 md:p-10 xl:p-12 max-w-6xl" data-testid={TESTIDS.organizationPage}>
      <div className="mb-10">
        <div className="kicker mb-3">— {t("org_kicker")}</div>
        <h1 className="font-serif text-4xl md:text-5xl mb-3">{t("org_title")}</h1>
        <p className="text-[var(--td-text-2)] max-w-2xl">{t("org_intro")}</p>
      </div>

      {!!mine.length && <section className="card-surface p-4 sm:p-6 mb-6 border-l-2 border-[var(--td-gold)]">
        <h2 className="font-serif text-xl mb-4">{t("org_incoming")}</h2>
        {mine.map((item) => <div key={item.id} className="flex items-center justify-between gap-4 py-2">
          <div><div className="font-serif">{item.organization_name}</div><div className="text-xs text-[var(--td-text-3)]">{t(`org_role_${item.role}`)}</div></div>
          <button onClick={() => accept(item)} data-testid={TESTIDS.organizationAccept} className="btn-gold text-xs">{t("org_accept")}</button>
        </div>)}
      </section>}

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <section className="card-surface p-4 sm:p-6 lg:col-span-2">
          <div className="flex items-center gap-2 mb-5"><Building2 size={16} className="text-[var(--td-gold)]" /><h2 className="font-serif text-2xl">{t("org_details")}</h2></div>
          <form onSubmit={saveName} className="flex gap-3 items-end">
            <div className="flex-1"><label className="td-label">{t("org_name")}</label><input disabled={!canManage} data-testid={TESTIDS.organizationName} className="td-input" value={name} onChange={(e) => setName(e.target.value)} /></div>
            {canManage && <button data-testid={TESTIDS.organizationSave} className="btn-gold">{t("ws_save")}</button>}
          </form>
        </section>
        <section className="card-surface p-4 sm:p-6">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--td-text-3)]">{t("org_seats")}</div>
          <div className="font-serif text-4xl mt-2">{organization?.seats_used || 0} / {organization?.seat_limit || 0}</div>
          <div className="text-xs text-[var(--td-text-3)] mt-2">{organization?.pending_invitations || 0} {t("org_pending")}</div>
        </section>
      </div>

      {canManage && <section className="card-surface p-4 sm:p-6 mb-6">
        <div className="flex items-center gap-2 mb-5"><MailPlus size={16} className="text-[var(--td-gold)]" /><h2 className="font-serif text-2xl">{t("org_invite_title")}</h2></div>
        <form onSubmit={sendInvite} className="grid md:grid-cols-[1fr_180px_auto] gap-3 items-end">
          <div><label className="td-label">{t("auth_email")}</label><input required type="email" data-testid={TESTIDS.organizationInviteEmail} className="td-input" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} /></div>
          <div><label className="td-label">{t("org_role")}</label><select data-testid={TESTIDS.organizationInviteRole} className="td-input" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })}>{ROLES.map((role) => <option key={role} value={role}>{t(`org_role_${role}`)}</option>)}</select></div>
          <button data-testid={TESTIDS.organizationInvite} className="btn-gold">{t("org_invite")}</button>
        </form>
      </section>}

      <section className="card-surface p-4 sm:p-6 mb-6">
        <div className="flex items-center gap-2 mb-4"><UserRoundCheck size={16} className="text-[var(--td-gold)]" /><h2 className="font-serif text-2xl">{t("org_members")}</h2></div>
        <div className="divide-y divide-[var(--td-border)]">{members.map((member) => <div key={member.id} data-testid={TESTIDS.organizationMemberRow} className="py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="min-w-0"><div className="font-serif">{member.full_name}</div><div className="font-mono text-[10px] text-[var(--td-text-3)] break-all">{member.email}</div></div>
          {canManage && member.role !== "owner" ? <div className="flex items-center gap-2 w-full sm:w-auto"><select className="td-input !py-1.5 text-xs flex-1 sm:!w-auto" value={member.role} onChange={(e) => changeRole(member, e.target.value)}>{ROLES.map((role) => <option key={role} value={role}>{t(`org_role_${role}`)}</option>)}</select><button onClick={() => removeMember(member)} className="w-11 h-11 inline-flex items-center justify-center text-[var(--td-text-3)] hover:text-red-400" title={t("org_remove")}><X size={15} /></button></div> : <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase text-[var(--td-gold)]"><ShieldCheck size={12} />{t(`org_role_${member.role}`)}</span>}
        </div>)}</div>
      </section>

      {canManage && !!invitations.length && <section className="card-surface p-4 sm:p-6">
        <h2 className="font-serif text-2xl mb-4">{t("org_pending_invites")}</h2>
        {invitations.map((item) => <div key={item.id} className="flex items-center justify-between gap-4 py-3 hairline-bottom"><div><div>{item.email}</div><div className="text-xs text-[var(--td-text-3)]">{t(`org_role_${item.role}`)}</div></div><button onClick={() => revoke(item)} className="text-[var(--td-text-3)] hover:text-red-400"><X size={15} /></button></div>)}
      </section>}
    </div>
  );
}
